// Top Mada — Page publique simplifiée
// 🎯 UN SEUL objectif : afficher les clips uploadés par l'admin + voter directement dessus.
// Pas de leaderboard artistes/morceaux, pas de Hall of Fame — uniquement les clips et le vote.

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getToken } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Vote, Play, Trophy, Calendar } from "lucide-react";

// ─── Helpers vidéo (extraction d'ID YouTube/Vimeo/TikTok) ───
const extractYouTubeId = (url) => {
  const m = url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};
const extractVimeoId = (url) => {
  const m = url?.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
};
const extractTikTokId = (url) => {
  const m = url?.match(/tiktok\.com\/.*?\/video\/(\d+)/);
  return m ? m[1] : null;
};

const VideoEmbed = ({ v }) => {
  const url = v.video_url;
  if (v.provider === "youtube") {
    const id = extractYouTubeId(url);
    if (id) return <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${id}`} title={v.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />;
  }
  if (v.provider === "vimeo") {
    const id = extractVimeoId(url);
    if (id) return <iframe className="w-full h-full" src={`https://player.vimeo.com/video/${id}`} title={v.title} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />;
  }
  if (v.provider === "tiktok") {
    const id = extractTikTokId(url);
    if (id) return <iframe className="w-full h-full" src={`https://www.tiktok.com/embed/v2/${id}`} title={v.title} allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" allowFullScreen />;
  }
  if (v.provider === "mp4") {
    return <video controls playsInline src={url} className="w-full h-full object-contain bg-black" />;
  }
  // Fallback : lien externe (Facebook, Instagram, autre)
  return (
    <a href={url} target="_blank" rel="noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/60 hover:bg-black/40 transition group">
      {v.thumbnail && <img src={v.thumbnail} alt={v.title} className="absolute inset-0 w-full h-full object-cover opacity-50" />}
      <div className="relative z-10 w-16 h-16 rounded-full bg-amber-400 flex items-center justify-center group-hover:scale-110 transition">
        <Play size={28} className="text-black ml-1" fill="black" />
      </div>
      <span className="absolute bottom-3 right-3 text-[10px] uppercase tracking-widest text-white/80 bg-black/60 px-2 py-0.5 rounded">Voir sur {v.provider}</span>
    </a>
  );
};

export default function TopMadaPage() {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(null);
  const isAuthed = !!getToken();

  useEffect(() => {
    document.title = "Top Mada · Vote pour les clips Malagasy · Voamada";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Top Mada — vote chaque jour pour tes clips Malagasy préférés. 1 vote / jour / clip.");
  }, []);

  const loadVideos = () => {
    setLoading(true);
    api.get("/topmada/videos")
      .then(({ data }) => setVideos(data.videos || []))
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadVideos(); }, []);

  useEffect(() => {
    api.get("/topmada/categories")
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const handleVote = async (videoId) => {
    if (!isAuthed) {
      toast.error("Connecte-toi pour voter");
      return;
    }
    setVoting(videoId);
    try {
      const { data } = await api.post("/topmada/vote", {
        target_type: "topmada_video",
        target_id: videoId,
        category_slug: "topmada_clip",
      });
      if (data.already_voted) {
        toast.info("Tu as déjà voté pour ce clip aujourd'hui");
      } else {
        toast.success("Vote enregistré 🇲🇬");
        // Met à jour localement (optimistic)
        setVideos((cur) =>
          cur.map((v) => v.video_id === videoId ? { ...v, has_voted: true, votes: (v.votes || 0) + 1 } : v)
        );
        // Badge débloqué ?
        if (Array.isArray(data.badges) && data.badges.length > 0) {
          const latest = data.badges[data.badges.length - 1];
          toast.success(`${latest.emoji} Badge débloqué : ${latest.label}`, { duration: 4000 });
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Erreur lors du vote");
    } finally {
      setVoting(null);
    }
  };

  // Filtre par catégorie de vote sélectionnée + tri par votes desc
  const filtered = videos.filter(v => {
    if (activeCat === "__all__") return true;
    const cats = Array.isArray(v.vote_categories) ? v.vote_categories : [];
    return cats.includes(activeCat);
  });
  const sorted = [...filtered].sort((a, b) => (b.votes || 0) - (a.votes || 0));
  const totalVotes = sorted.reduce((s, v) => s + (v.votes || 0), 0);

  // Catégories à afficher comme tabs : "Tous" + celles qui ont au moins 1 clip rattaché
  const usedCats = new Set();
  videos.forEach(v => (v.vote_categories || []).forEach(s => usedCats.add(s)));
  const tabCats = categories.filter(c => usedCats.has(c.slug));
  const showTabs = tabCats.length >= 1; // dès qu'il y a au moins 1 catégorie utilisée

  return (
    <div className="min-h-screen bg-black text-white" data-testid="topmada-page">
      {/* HERO */}
      <header className="relative overflow-hidden border-b border-amber-400/20">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(800px 300px at 50% 0%, rgba(252,211,77,0.45), transparent 70%)" }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 text-center">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="/assets/topmada-logo.png"
              alt="Top Mada"
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-2 ring-amber-300/60 shadow-[0_0_40px_-5px_rgba(255,170,40,0.6)]"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-orange-400 bg-clip-text text-transparent">TOP MADA</span>
          </h1>
          <p className="mt-3 text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
            Les clips officiels Malagasy en lice. <br className="hidden sm:block" />
            <span className="text-amber-200 font-semibold">Vote chaque jour</span> pour ton clip préféré 🇲🇬
          </p>
          <div className="mt-5 flex items-center justify-center gap-4 text-xs sm:text-sm text-white/60 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Trophy size={14} className="text-amber-300" /> {sorted.length} clip{sorted.length > 1 ? "s" : ""}</span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5"><Vote size={14} className="text-fuchsia-300" /> {totalVotes} vote{totalVotes > 1 ? "s" : ""}</span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> 1 vote/jour/clip</span>
          </div>
        </div>
      </header>

      {/* CTA Login si non authed */}
      {!isAuthed && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
          <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-4 text-center">
            <p className="text-sm text-white/85">
              <Link to="/login?redirect=/topmada" data-testid="topmada-login-cta" className="text-amber-300 font-bold hover:underline">Connecte-toi</Link>
              {" "}pour voter et débloquer tes badges Fan TopMada ⭐
            </p>
          </div>
        </div>
      )}

      {/* GRID des clips */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-24">
        {/* Tabs filtres par catégorie de vote */}
        {showTabs && (
          <div className="mb-6 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="topmada-category-tabs">
            <button
              onClick={() => setActiveCat("__all__")}
              data-testid="topmada-tab-all"
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition border whitespace-nowrap ${
                activeCat === "__all__"
                  ? "bg-amber-300 text-black border-amber-200 shadow-[0_0_24px_-8px_rgba(255,200,80,0.6)]"
                  : "bg-white/[0.04] text-white/65 border-white/15 hover:bg-white/[0.08] hover:text-white"
              }`}
            >
              🏆 Tous ({videos.length})
            </button>
            {tabCats.map(c => {
              const count = videos.filter(v => (v.vote_categories || []).includes(c.slug)).length;
              const emoji = c.kind === "topmada_video" ? "🎬" : c.kind === "track" ? "🎵" : "🎤";
              const active = activeCat === c.slug;
              return (
                <button
                  key={c.slug}
                  onClick={() => setActiveCat(c.slug)}
                  data-testid={`topmada-tab-${c.slug}`}
                  className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition border whitespace-nowrap ${
                    active
                      ? "bg-amber-300 text-black border-amber-200 shadow-[0_0_24px_-8px_rgba(255,200,80,0.6)]"
                      : "bg-white/[0.04] text-white/65 border-white/15 hover:bg-white/[0.08] hover:text-white"
                  }`}
                >
                  {emoji} {c.label} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Podium "Champions en cours" — visible quand au moins 3 clips dans la sélection */}
        {!loading && sorted.length >= 3 && (
          <CategoryPodium
            top3={sorted.slice(0, 3)}
            label={activeCat === "__all__" ? "Top Mada Général" : (categories.find(c => c.slug === activeCat)?.label || "Catégorie")}
          />
        )}

        {loading ? (
          <div className="text-center py-16 text-white/50">
            <Loader2 size={28} className="animate-spin mx-auto mb-3" />
            Chargement des clips Top Mada…
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16">
            <Trophy size={36} className="mx-auto mb-3 text-white/20" />
            {videos.length === 0 ? (
              <>
                <p className="text-white/60 font-semibold">Aucun clip disponible pour le moment</p>
                <p className="text-white/40 text-sm mt-1">Reviens bientôt — l'admin ajoute régulièrement de nouveaux clips !</p>
              </>
            ) : (
              <>
                <p className="text-white/60 font-semibold">Aucun clip dans cette catégorie</p>
                <p className="text-white/40 text-sm mt-1">
                  Bascule sur un autre filtre ou clique{" "}
                  <button onClick={() => setActiveCat("__all__")} className="text-amber-300 underline">Tous</button>{" "}
                  pour voir l'ensemble.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="topmada-clips-grid">
            {sorted.map((v, idx) => {
              const rank = idx + 1;
              const isPodium = rank <= 3;
              const rankColors = ["text-amber-300", "text-zinc-200", "text-orange-400"];
              return (
                <article
                  key={v.video_id}
                  data-testid={`topmada-clip-${v.video_id}`}
                  className={`group rounded-2xl overflow-hidden border transition-all hover:-translate-y-1 ${
                    isPodium
                      ? "bg-amber-400/[0.06] border-amber-400/30 shadow-[0_0_30px_-15px_rgba(252,211,77,0.5)]"
                      : "bg-zinc-900/80 border-white/10 hover:border-amber-400/40"
                  }`}
                >
                  {/* Rank badge */}
                  <div className="relative aspect-video bg-black">
                    <VideoEmbed v={v} />
                    {isPodium && (
                      <span className={`absolute top-2 left-2 z-10 px-2 py-1 rounded-md text-xs font-black bg-black/70 backdrop-blur ${rankColors[rank - 1]}`}>
                        #{rank}
                      </span>
                    )}
                    <span className="absolute top-2 right-2 z-10 px-2 py-1 rounded-md text-[10px] font-bold bg-black/70 backdrop-blur text-amber-200">
                      🗳️ {v.votes || 0}
                    </span>
                  </div>

                  <div className="p-3 space-y-2">
                    <h3 className="font-bold text-sm leading-tight line-clamp-2">{v.title}</h3>
                    {Array.isArray(v.vote_categories) && v.vote_categories.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {v.vote_categories.map(slug => (
                          <span
                            key={slug}
                            data-testid={`clip-vote-cat-${slug}`}
                            className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-200 border border-amber-400/30"
                          >
                            🏆 {slug.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    )}
                    {v.description && <p className="text-xs text-white/60 line-clamp-2">{v.description}</p>}

                    <div className="flex items-center justify-between text-[10px] text-white/45">
                      {v.artist_id && v.artist_name ? (
                        <Link to={`/artist/${v.artist_id}`} className="hover:text-amber-300 transition flex items-center gap-1.5 min-w-0">
                          {v.artist_picture && <img src={v.artist_picture} alt="" className="w-4 h-4 rounded-full object-cover" />}
                          <span className="truncate">{v.artist_name}</span>
                        </Link>
                      ) : <span />}
                      <span className="shrink-0">{(v.views || 0).toLocaleString()} vues</span>
                    </div>

                    <button
                      onClick={() => handleVote(v.video_id)}
                      disabled={voting === v.video_id || v.has_voted || !isAuthed}
                      data-testid={`topmada-vote-btn-${v.video_id}`}
                      className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition disabled:cursor-not-allowed ${
                        !isAuthed
                          ? "bg-white/[0.05] border border-white/10 text-white/40"
                          : v.has_voted
                            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/40"
                            : "bg-gradient-to-r from-amber-300 to-orange-400 text-black hover:brightness-110 active:scale-95"
                      }`}
                    >
                      {voting === v.video_id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : v.has_voted ? (
                        <>✓ Voté aujourd'hui</>
                      ) : !isAuthed ? (
                        <>🔒 Connecte-toi pour voter</>
                      ) : (
                        <><Vote size={14} /> Voter pour ce clip</>
                      )}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Footer info */}
        <div className="mt-12 text-center text-xs text-white/40 space-y-1">
          <p className="inline-flex items-center gap-1.5"><Calendar size={12} /> Classement en temps réel · 1 vote/jour/clip</p>
          <p>Propulsé par <span className="text-amber-200 font-semibold">Top Mada</span> · Voamada © 2026 🇲🇬</p>
        </div>
      </main>
    </div>
  );
}

// ─── Podium "Champion en cours" (3 premiers d'une catégorie) ─────
const CategoryPodium = ({ top3, label }) => {
  if (!top3 || top3.length < 3) return null;
  // Ordre visuel : 2 - 1 - 3 (le #1 au centre, plus haut)
  const visual = [top3[1], top3[0], top3[2]];
  const ranks = [2, 1, 3];
  const heights = ["h-24 sm:h-28", "h-32 sm:h-40", "h-20 sm:h-24"];
  const colors = [
    "from-zinc-300 to-zinc-500 text-zinc-900 shadow-[0_0_30px_-10px_rgba(220,220,220,0.6)]",
    "from-amber-300 to-yellow-500 text-black shadow-[0_0_40px_-8px_rgba(252,211,77,0.8)]",
    "from-orange-400 to-amber-700 text-orange-50 shadow-[0_0_30px_-10px_rgba(251,146,60,0.6)]",
  ];
  const medals = ["🥈", "🥇", "🥉"];

  return (
    <section
      data-testid="topmada-podium"
      className="mb-8 rounded-3xl border border-amber-400/30 bg-gradient-to-b from-amber-950/30 to-black/60 px-4 py-6 sm:px-8 sm:py-8 overflow-hidden relative"
    >
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 bg-amber-400/15 blur-3xl rounded-full pointer-events-none" />

      <div className="relative text-center mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 font-bold">🏆 Champions en cours</p>
        <h2 className="text-xl sm:text-2xl font-black mt-1 bg-gradient-to-r from-amber-200 via-amber-400 to-orange-400 bg-clip-text text-transparent">
          {label}
        </h2>
      </div>

      <div className="relative grid grid-cols-3 gap-2 sm:gap-4 items-end max-w-3xl mx-auto">
        {visual.map((v, i) => {
          const rank = ranks[i];
          return (
            <div key={v.video_id} className="flex flex-col items-center" data-testid={`podium-rank-${rank}`}>
              {/* Card du clip */}
              <div className={`w-full rounded-2xl overflow-hidden border-2 ${rank === 1 ? "border-amber-300" : "border-white/20"} bg-black mb-2 shadow-xl`}>
                <div className="relative aspect-video">
                  {v.thumbnail ? (
                    <img
                      src={v.thumbnail.startsWith("http") ? v.thumbnail : `https://www.voamada.com${v.thumbnail}`}
                      alt={v.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-700/40 to-red-700/40" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 text-[10px] font-bold text-amber-200">
                    🗳️ {v.votes || 0}
                  </span>
                </div>
                <div className="px-2 py-1.5 text-center">
                  <p className="text-[10px] sm:text-xs font-bold leading-tight line-clamp-2">{v.title}</p>
                </div>
              </div>

              {/* Podium step (la "marche" en or/argent/bronze) */}
              <div className={`w-full ${heights[i]} rounded-t-xl bg-gradient-to-b ${colors[i]} flex flex-col items-center justify-center font-extrabold relative`}>
                <span className="text-2xl sm:text-3xl">{medals[i]}</span>
                <span className="text-base sm:text-lg leading-none mt-0.5">#{rank}</span>
                {rank === 1 && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-black uppercase tracking-widest bg-black text-amber-300 px-2 py-0.5 rounded-full whitespace-nowrap">
                    Champion
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
